import './input.css'

import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Services } from '@/services/services'
import { FieldProps, withFieldWrap } from '@/components/forms/common/FormBase'
import { Controller, FieldValues } from 'react-hook-form'
import { Translates } from '@/services/repository/i18n'

export const CommonUCIDInput = (props: {
  fieldName: string
  formName: string
  translates: Translates
  value: string
  setValue: (v: string) => void
  readOnly?: boolean
  services: Services
}) => {
  const [showNFCPopup, setShowNFCPopup] = useState(false)
  const [nfcMessage, setNFCMessage] = useState<string | null>(null)
  const ndefReader = useRef<NDEFReader | null>(null)
  const abortController = useRef<AbortController | null>(null)
  const aborted = useRef(false)
  const isNFCSupported = "NDEFReader" in window

  const startScan = async () => {
    if (!isNFCSupported) {
      console.error("Web NFC is not supported on this browser.");
      return;
    }
    aborted.current = false;
    ndefReader.current = new NDEFReader();
    abortController.current = new AbortController();

    setShowNFCPopup(true);

    try {
      await ndefReader.current.scan({ signal: abortController.current.signal });
      ndefReader.current.onreadingerror = () => {
        setNFCMessage("inputs.ucid.read_nfc_tag_error")
        console.error("Cannot read data from the NFC tag.")
      };
      ndefReader.current.onreading = (event) => {
        if (aborted.current) return;
        const tagId = event.serialNumber || null;
        if (tagId) {
          const ucid = tagId.replaceAll(':', '').toUpperCase()
          props.setValue(ucid)
          setShowNFCPopup(false)
        }
      };
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message);
      } else {
        console.error(String(err));
      }
      setShowNFCPopup(false);
    }
  }

  const closeNFCPopup = () => {
    setShowNFCPopup(false);
    aborted.current = true;
    if (abortController.current) abortController.current.abort();
  }

  useEffect(() => {
    return () => {
      aborted.current = true;
      if (abortController.current) abortController.current.abort();
    }
  }, [])

  return <>
    {isNFCSupported ? <div className="code-text-fild">
      <input
        type="text"
        id={props.fieldName}
        value={props.value}
        name={props.fieldName}
        readOnly={props.readOnly}
        onChange={(e) => props.setValue(e.target.value)}
        placeholder={props.translates.for(`inputs.ucid.placeholder`)}
      />
      <div className="toolbox" onClick={props.readOnly ? undefined : startScan}>
        <div className="icon-button">
          <i className="svgfont-nfc"></i>
        </div>
      </div>
    </div> : <input
      type="text"
      id={props.fieldName}
      value={props.value}
      name={props.fieldName}
      readOnly={props.readOnly}
      onChange={(e) => props.setValue(e.target.value)}
      placeholder={props.translates.for(`inputs.ucid.placeholder`)}
    />}

    {showNFCPopup && ReactDOM.createPortal(
      <div className='nfc-popup'>
        <div onClick={closeNFCPopup} className='nfc-overlay'>
          <div className='nfc-popup'>
            <div>
              <i className='svgfont-nfc' />
            </div>
            {nfcMessage ?
              <div className='form-message'>{props.translates.for(nfcMessage)}</div>
              : <div>{props.translates.for('inputs.ucid.insert_nfc_card')}</div>}
          </div>
        </div>
      </div>,
      document.querySelector('.app-wrap') || document.body
    )}
  </>
}

export const UCIDInput = <T extends FieldValues>(props: FieldProps<T>) => {
  return <Controller
    name={props.fieldName}
    control={props.form.control}
    rules={{
      required: props.required ? props.translates.for(`forms.${props.formName}.errors.fields.${props.fieldName}.required`) : undefined,
    }}
    render={({
      field: { value, onChange },
    }) => {
      return <CommonUCIDInput
        fieldName={props.fieldName}
        formName={props.formName}
        translates={props.translates}
        value={value}
        setValue={onChange}
        readOnly={props.readOnly}
        services={props.services}
      />
    }}
  />
}

export const ucidField = <T extends FieldValues>() => withFieldWrap<T>(UCIDInput<T>)

